import React, { useEffect, useState } from "react";
import { adminApi } from "@/api/adminOneClient";

const TIER_COLORS = { free: "#60B8E8", professional: "#7AC860", executive: "#F5C842", enterprise: "#E8A020" };

export default function AdminTelemetry() {
  const [byFeature, setByFeature] = useState([]);
  const [byTier, setByTier] = useState([]);
  const [byEvent, setByEvent] = useState([]);
  const [ts, setTs] = useState([]);
  const [recent, setRecent] = useState([]);
  const [hours, setHours] = useState(24);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [f, t, e, series, r] = await Promise.all([
        adminApi.telemetryByFeature(hours),
        adminApi.telemetryByTier(hours),
        adminApi.telemetryByEvent(hours),
        adminApi.telemetryTimeseries(hours, 60),
        adminApi.telemetryRecent(60),
      ]);
      setByFeature(f.data?.rows || []);
      setByTier(t.data?.rows || []);
      setByEvent(e.data?.rows || []);
      setTs(series.data?.buckets || []);
      setRecent(r.data?.events || []);
    } finally { setLoading(false); }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [hours]);

  const maxFeature = Math.max(1, ...byFeature.map((r) => r.count));
  const maxTier    = Math.max(1, ...byTier.map((r) => r.count));
  const maxBucket  = Math.max(1, ...ts.map((b) => b.count));

  return (
    <div className="admin-page mesh-shell" data-testid="admin-telemetry">
      <div className="mesh-bg-radar" aria-hidden="true" />
      <div className="mesh-bg-particles" aria-hidden="true" />
      <div className="admin-page__head">
        <h2 className="admin-page__title">Usage Telemetry</h2>
        <div className="mesh-eyebrow">v13 · Per-feature • Per-tier • Per-event • Timeseries</div>
      </div>

      <section className="admin-section mesh-section">
        <div className="admin-filters" style={{ marginBottom: 10 }}>
          <label className="mesh-eyebrow" style={{ marginRight: 8 }}>Window</label>
          {[1, 6, 24, 72, 168].map((h) => (
            <button key={h} type="button"
                    className={`admin-linkbtn ${hours === h ? "is-active" : ""}`}
                    onClick={() => setHours(h)} data-testid={`telemetry-hours-${h}`}>
              {h <= 24 ? `${h}h` : `${h/24}d`}
            </button>
          ))}
          <button type="button" className="admin-linkbtn" onClick={load} data-testid="telemetry-refresh">Refresh</button>
        </div>
        {loading && <div className="admin-saved">Loading…</div>}
      </section>

      <section className="admin-section mesh-section" data-testid="telemetry-timeseries">
        <h3 className="admin-section__title">Usage Timeseries</h3>
        <div className="mesh-spark">
          {ts.map((b, i) => (
            <span key={i} className="mesh-spark__bar"
                  style={{ height: `${Math.max(2, (b.count / maxBucket) * 100)}%` }}
                  title={`${new Date(b.bucket_start).toLocaleTimeString()} · ${b.count}`} />
          ))}
        </div>
      </section>

      <section className="admin-section mesh-section" data-testid="telemetry-by-feature">
        <h3 className="admin-section__title">By Feature</h3>
        <div className="mship-bars">
          {byFeature.map((r) => (
            <div key={r.feature} className="mship-bar-row" data-testid={`telemetry-feature-${r.feature}`}>
              <div className="mship-bar-row__lbl">{r.feature}</div>
              <div className="mship-bar-row__bar"><span style={{ width: `${(r.count / maxFeature) * 100}%`, background: "#F5C842" }} /></div>
              <div className="mship-bar-row__num">{r.count} <span className="mship-bar-row__pct">({r.granted} granted, {r.denied} denied)</span></div>
              <div className="mship-bar-row__price" />
            </div>
          ))}
          {byFeature.length === 0 && <div className="admin-foot-note">No feature events in this window.</div>}
        </div>
      </section>

      <section className="admin-section mesh-section" data-testid="telemetry-by-tier">
        <h3 className="admin-section__title">By Tier</h3>
        <div className="mship-bars">
          {byTier.map((r) => (
            <div key={r.tier} className="mship-bar-row" data-testid={`telemetry-tier-${r.tier}`}>
              <div className="mship-bar-row__lbl" style={{ color: TIER_COLORS[r.tier] }}>{(r.tier || "").toUpperCase()}</div>
              <div className="mship-bar-row__bar"><span style={{ width: `${(r.count / maxTier) * 100}%`, background: TIER_COLORS[r.tier] || "#F5C842" }} /></div>
              <div className="mship-bar-row__num">{r.count}</div>
              <div className="mship-bar-row__price" />
            </div>
          ))}
          {byTier.length === 0 && <div className="admin-foot-note">No tier events in this window.</div>}
        </div>
      </section>

      <section className="admin-section mesh-section" data-testid="telemetry-by-event">
        <h3 className="admin-section__title">By Event Type</h3>
        <div className="mesh-pill-grid">
          {byEvent.map((r) => (
            <span key={r.event} className="mesh-pill" style={{ borderColor: "#F5C842", color: "#F5C842" }} data-testid={`telemetry-event-${r.event}`}>
              {r.event} <strong style={{ marginLeft: 4, color: "#fff" }}>{r.count}</strong>
            </span>
          ))}
          {byEvent.length === 0 && <div className="admin-foot-note">No events.</div>}
        </div>
      </section>

      <section className="admin-section mesh-section" data-testid="telemetry-recent">
        <h3 className="admin-section__title">Recent Events</h3>
        <table className="admin-table">
          <thead><tr><th>When</th><th>Event</th><th>Feature</th><th>Tier</th><th>User</th><th>Granted</th></tr></thead>
          <tbody>
            {recent.map((e) => (
              <tr key={e.id || e._id}>
                <td>{e.created_at?.slice(11, 19)}</td>
                <td><span className="mesh-pill mesh-pill--ghost">{e.event}</span></td>
                <td>{e.feature || "—"}</td>
                <td>{e.tier ? <span className="mesh-pill" style={{ borderColor: TIER_COLORS[e.tier], color: TIER_COLORS[e.tier] }}>{e.tier}</span> : "—"}</td>
                <td>{e.user_id || "—"}</td>
                <td>{e.granted === true ? "✓" : e.granted === false ? "✕" : "—"}</td>
              </tr>
            ))}
            {recent.length === 0 && <tr><td colSpan={6} className="admin-foot-note">No events.</td></tr>}
          </tbody>
        </table>
      </section>
    </div>
  );
}
